import React, { useState } from "react";
/**Components */
import { CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Minus, Plus, Search } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import SingleSelect from "@/pages/components/single-slect";
import SearchAutocomplete from "@/pages/components/search-autocomplete";
/**Hooks */
import { useDispatch, useSelector } from "react-redux";
import type { ICostEstFilterParams } from "@/hooks/use-cost-est-filter";
import useCostEstFilter from "@/hooks/use-cost-est-filter";
import type {
  INetworkResult,
  ISpecialityResult,
} from "@/hooks/use-cost-estimator";
import {
  useFacilitySearch,
  useLocationSearch,
  useProviderSearch,
} from "@/hooks/use-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
/**Models */
import type Facility from "@/model/facility";
import type Place from "@/model/place";
import type Provider from "@/model/provider";
import type Speciality from "@/model/speciality";
import type Network from "@/model/network";
import type Distance from "@/model/distance";
import type Pos from "@/model/pos";
/**Constants */
import { ALL_POS, DISTANCE_OPTIONS, QueryKeys } from "@/constants";
import {
  setDistance,
  setFacility,
  setLocation,
  setPos,
  setSpecialities,
  setTiers,
  setNetworks,
  setProvider,
} from "@/store/price transparency/price-transparency-slice";
import { queryClient } from "@/hooks/use-query-client";
import type { RootState } from "@/store/store";
import {
  getNetworksFromTiers,
  getTiersFromNetworks,
} from "@/lib/cost-estimator-utils";
import CostEstimatorMultiSelect from "./cost-estimator-multi-select";
import type { Tier } from "@/types";

const CostEstimatorFilter: React.FC<{
  onSearch: () => void;
  onClear: () => void;
}> = ({ onSearch, onClear }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const costEstFilterParams = useCostEstFilter();
  const userInfo = useUserInfo();

  const locationSearchQuery = useLocationSearch<Place>(userInfo); //run on every search
  const providerSearchQuery = useProviderSearch<Provider>(userInfo);
  const facilitySearchQuery = useFacilitySearch<Facility>(userInfo);

  const networkData = queryClient.getQueryData<INetworkResult>([
    QueryKeys.NETWORKS,
    userInfo.username,
  ]);
  const specialitiesData = queryClient.getQueryData<ISpecialityResult>([
    QueryKeys.SPECIALITIES,
    userInfo.username,
    costEstFilterParams.procedure?.code ?? "",
  ]);

  const {
    specialities,
    pos,
    networks,
    tiers,
    distance,
    transformedNetworkTier,
  } = useSelector((state: RootState) => state.priceTransparency);

  return (
    <div>
      <div className="grid gap-8 px-6 py-4 lg:grid-cols-3">
        {/* Search Criteria */}
        <div className="space-y-6 pt-1 lg:col-span-1">
          <h2 className="text-md font-semibold">Search Criteria</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold">Procedure:</Label>
              <Label className="text-sm font-light text-muted-foreground">
                {costEstFilterParams.procedure?.description ?? ""} (
                {costEstFilterParams.procedure?.code})
              </Label>
            </div>
            <div className="space-y-2">
              <SearchAutocomplete<Place, { queryString: string }>
                label="Location"
                placeholder="Enter a location"
                placeholderIcon={MapPin}
                value={costEstFilterParams.targetLocation?.description}
                queryFunction={locationSearchQuery}
                onSelect={async (value: Place) => {
                  dispatch(setLocation(value));
                }}
                formatResult={(result: Place, _: number) => {
                  return (
                    <div key={result.place_id}>
                      <div className="text-sm font-medium">
                        {result.description}
                      </div>
                    </div>
                  );
                }}
                onClear={() => {
                  dispatch(setLocation(null));
                }}
                minSearchLength={2}
                debounceTime={200}
              />
            </div>
            <div className="space-y-2">
              <Label>Distance</Label>
              <SingleSelect
                placeholder="Select Distance"
                data={DISTANCE_OPTIONS}
                defaultOption={costEstFilterParams.distance}
                readValueFor="displayName"
                selectedOption={distance}
                searchEnabled={false}
                onSelect={(item: Distance) => {
                  dispatch(setDistance(item));
                }}
              />
            </div>
          </div>
        </div>
        {/* Additional Filters */}
        <div className="space-y-6 lg:col-span-2">
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex items-center justify-between">
              <Label className="text-md font-semibold">
                Filters{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  (Click to view or modify applied filters.)
                </span>
              </Label>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  {isOpen ? (
                    <Minus className="size-4" />
                  ) : (
                    <Plus className="size-4" />
                  )}
                  <span className="sr-only">Toggle filters</span>
                </Button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="space-y-4">
              <div className="mt-4 flex items-center gap-2">
                <Label className="text-sm font-semibold">Network Status:</Label>
                <Label className="text-sm font-light text-muted-foreground">
                  In Network
                </Label>
              </div>
              <div className="grid gap-4 space-y-1 lg:grid-cols-2">
                {/* Provider Specialty */}
                <div className="space-y-3">
                  <Label>Provider Specialty</Label>
                  <CostEstimatorMultiSelect<Speciality>
                    data={specialitiesData!.all}
                    searchKey="categorySpecialty"
                    displayKey="categorySpecialty"
                    selected={specialities}
                    lookupKey="categorySpecialty"
                    defaultValues={specialitiesData?.default}
                    onChange={(updatedSpecialities: Speciality[]) => {
                      dispatch(setSpecialities(updatedSpecialities));
                    }}
                    enableSearch
                    placeholder="Select Provider Specialty"
                    atLeastOneRequired
                    enableSelectAll
                  />
                </div>
                {/* Place of Service */}
                <div className="space-y-2">
                  <Label>Place of Service</Label>
                  <CostEstimatorMultiSelect<Pos>
                    data={ALL_POS}
                    displayKey="displayName"
                    selected={pos}
                    lookupKey="code"
                    defaultValues={[]}
                    onChange={(updatedPos: Pos[]) => {
                      dispatch(setPos(updatedPos));
                    }}
                    placeholder="Select Place of Service"
                    enableSelectAll
                  />
                </div>
                {/* Facility Name/ID */}
                <div className="space-y-2">
                  <SearchAutocomplete<
                    Facility,
                    {
                      queryString: string;
                      filter: ICostEstFilterParams;
                      url?: string;
                    }
                  >
                    label="Facility Name/ID"
                    placeholder="Enter a facility name or ID"
                    placeholderIcon={Search}
                    value={costEstFilterParams.facility?.code}
                    additionalParams={{
                      queryString: "",
                      filter: costEstFilterParams,
                    }}
                    queryFunction={facilitySearchQuery}
                    formatResult={(result: Facility, index: number) => {
                      return (
                        <div key={index}>
                          <span className="text-sm font-medium">
                            {result.description}
                          </span>
                          <div className="text-xs text-muted-foreground">
                            TIN: {result.code}
                          </div>
                        </div>
                      );
                    }}
                    onSelect={(value: Facility) => {
                      dispatch(setFacility(value));
                    }}
                    onClear={() => {
                      dispatch(setFacility(null));
                    }}
                    key="code"
                    minSearchLength={3}
                    debounceTime={200}
                  />
                </div>

                {/* Physician Name/NPI */}
                <div className="space-y-2">
                  <SearchAutocomplete<
                    Provider,
                    {
                      queryString: string;
                      filter: ICostEstFilterParams;
                      url?: string;
                    }
                  >
                    label="Physician Name/NPI"
                    placeholder="Enter a physician name or NPI"
                    placeholderIcon={Search}
                    value={costEstFilterParams.provider?.code}
                    additionalParams={{
                      queryString: "",
                      filter: costEstFilterParams,
                    }}
                    queryFunction={providerSearchQuery}
                    formatResult={(result: Provider, index: number) => {
                      return (
                        <div key={index}>
                          <span className="text-sm font-medium">
                            {result.description}
                          </span>
                          <div className="text-xs text-muted-foreground">
                            TIN: {result.code}
                          </div>
                        </div>
                      );
                    }}
                    onSelect={(value: Provider) => {
                      dispatch(setProvider(value));
                    }}
                    onClear={() => {
                      dispatch(setProvider(null));
                    }}
                    key="code"
                    minSearchLength={3}
                    debounceTime={200}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Network Tiers</Label>
                  <CostEstimatorMultiSelect<Tier>
                    data={networkData!.tiers}
                    displayKey="name"
                    selected={tiers}
                    lookupKey="name"
                    defaultValues={costEstFilterParams.tiers}
                    onChange={(updatedTiers: { name: string }[]) => {
                      const updatedNetworks = getNetworksFromTiers(
                        updatedTiers,
                        transformedNetworkTier!,
                      );
                      dispatch(setTiers(updatedTiers));
                      dispatch(setNetworks(updatedNetworks));
                    }}
                    placeholder="Select Tiers"
                    atLeastOneRequired
                  />
                </div>
                <div className="space-y-2">
                  <Label>Network Name</Label>
                  <CostEstimatorMultiSelect<Network>
                    data={networkData!.networks}
                    displayKey="networkName"
                    selected={networks}
                    lookupKey="networkName"
                    onChange={(updatedNetworks: Network[]) => {
                      const updatedNetworkTiers =
                        getTiersFromNetworks(updatedNetworks);
                      const uniqueNetworks = [
                        ...new Map(
                          updatedNetworks.map((network) => [
                            network.networkName,
                            network,
                          ]),
                        ).values(),
                      ];
                      dispatch(setNetworks(uniqueNetworks));
                      dispatch(setTiers(updatedNetworkTiers));
                    }}
                    placeholder="Select Network"
                    atLeastOneRequired
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
      <CardFooter className="flex justify-end gap-2 border-t py-2">
        <Button variant="outline" onClick={() => onClear()}>
          Clear
        </Button>
        <Button
          type="button"
          variant="default"
          className="hover:bg-primary-selected"
          onClick={() => onSearch()}
          disabled={
            !costEstFilterParams.procedure ||
            !costEstFilterParams.targetLocation ||
            !costEstFilterParams.distance
          }
        >
          Search
        </Button>
      </CardFooter>
    </div>
  );
};

export default CostEstimatorFilter;
